import { identity, isEmpty, isNil, omit } from "lodash";
import { makeAutoObservable, runInAction } from "mobx";
import {
  EnterpriseAccount,
  failure,
  isOk,
  ok,
  pending,
  ProductName,
  Result,
  SubscriptionTypeModel,
} from "../common/common";
import { CURRENCY_NOK, formatPrice } from "../utils/currency";
import { PaymentService } from "./PaymentApi";
import { logDebug, logError } from "../utils/log";
import { getReturnToAfterPaymentUrl } from "../utils/navigation";
import { Affiliate } from "../affiliate/Affiliate";
import { AxiosInstance } from "axios";
import { Language } from "../i18n/languages";
import { TFunction, TMessage } from "../i18n/tt";
import { TMessageKey } from "../i18n/types.gen";

import { isPositive, toNumber } from "../utils/numbers";
import { isNotBlank } from "../utils/strings";
import { RadioFieldModel } from "../common/commonForms";

import {
  PaidSubscription,
  SubscriptionStatusContext,
  SubscriptionType,
} from "../common/subscription";
import { DCEvent } from "../events/eventsDomain";
import { fromQueryString, toQueryString } from "../utils/queryParams";
import {
  ActivationMethod,
  BillInterval,
  PricingTier,
  ProductDescription,
  ProductPriceDescription,
} from "../common/product";

export enum PaymentType {
  NONE = "NONE",
  STRIPE = "STRIPE",
  INVOICE = "INVOICE",
}

export type UpgradeSubscriptionRequest = {
  subscriptionId: string;
  organisationNumber: string;
  productPriceId: string;
};

export type CreateInvoiceSubscriptionRequest = {
  subscriptionType: string;
  productPriceId: string;
};

export type CreatePaymentUrlRequest = {
  productPriceId: string;
  returnToUrl: string;
};

export type CreatePaymentUrlResponse = {
  redirectToPaymentUrl: string;
};

export type PaymentDetailsResponse = {
  authenticatedUrl: string;
  latestPayment?: Payment;
};

export type ListProductResponse = {
  products: ProductDescription[];
  inactiveProducts: ProductDescription[];
};

export type ProductSubscriptionResponse = {
  subscriptionId: string;
  subscriptionType: SubscriptionType;
  productPriceId: string;
  paymentMethod: PaymentType;
  subscriptionStartDate: string;
  subscriptionEndDate?: string;
  active: boolean;
  billInterval: BillInterval;
  paidAmount: number;
  productDescription: ProductDescription;
  productId?: string;
  productName?: ProductName;
  signUpMethod?: SignupMethod;
  paidSubscription?: PaidSubscription;
  paymentDetails?: PaymentDetailsResponse;
};

export type EnterpriseAccountsForUserResponse = {
  enterpriseAccounts: EnterpriseAccount[];
};

export enum SignupMethod {
  SELF_SERVICE = "SELF_SERVICE",
  DCOMPANY = "DCOMPANY",
}

export const timeUnitLabel: { [key in BillInterval]: TMessageKey } = {
  [BillInterval.MONTH]: "settings.subscription.perTimeUnit.month",
  [BillInterval.YEAR]: "settings.subscription.perTimeUnit.year",
};

const billIntervalLabels: Record<BillInterval, TMessage> = {
  [BillInterval.MONTH]: { key: "billinterval.monthly" },
  [BillInterval.YEAR]: { key: "billinterval.annually" },
};

const paymentTypeLabels: Record<PaymentType, TMessage> = {
  [PaymentType.NONE]: { translated: "none" },
  [PaymentType.STRIPE]: { key: "payment.subscriptionpopup.cardOption.label" },
  [PaymentType.INVOICE]: { key: "payment.subscriptionpopup.invoiceOption.label" },
};

export enum PaymentStatus {
  PAID = "PAID",
  FAILED = "FAILED",
}

export enum StripePaymentStatus {
  Created = "Created",
  Cancelled = "Cancelled",
  Completed = "Completed",
}

export function handlePaymentResult(pathname: string, queryString: string): DCEvent[] | undefined {
  const query = fromQueryString(queryString);
  const paymentStatus = query["paymentStatus"];

  if (paymentStatus && paymentStatus in StripePaymentStatus) {
    switch (paymentStatus as StripePaymentStatus) {
      case StripePaymentStatus.Completed:
        return [
          { type: "navigate", url: pathname + toQueryString(omit(query, "paymentStatus")) },
          {
            type: "show-alert",
            message: { key: "payment.stripe-success-message" },
            messageType: "success",
          },
        ];
      case StripePaymentStatus.Cancelled:
        return [
          { type: "navigate", url: pathname + toQueryString(omit(query, "paymentStatus")) },
          {
            type: "show-alert",
            message: { key: "payment.stripe-failure-message" },
            messageType: "error",
          },
        ];
    }
  }
}

export type Payment = {
  status: PaymentStatus;
  invoicePdfUrl: string;
  createdTime: string;
};

export function requiresSubscription(subscriptionTypeModel?: SubscriptionTypeModel) {
  if (isNil(subscriptionTypeModel)) {
    return false;
  }
  return (
    requiresBasicSubscription(subscriptionTypeModel) ||
    requiresProSubscription(subscriptionTypeModel)
  );
}

export function requiresBasicSubscription(subscriptionTypeModel?: SubscriptionTypeModel) {
  if (isNil(subscriptionTypeModel)) {
    return false;
  }
  return (
    subscriptionTypeModel.requiredSubscriptionType === SubscriptionType.BASIC &&
    subscriptionTypeModel.currentSubscription?.subscriptionType !== SubscriptionType.BASIC &&
    subscriptionTypeModel.currentSubscription?.subscriptionType !== SubscriptionType.PRO &&
    subscriptionTypeModel.currentSubscription?.allowsDbot !== true
  );
}

export function requiresProSubscription(subscriptionTypeModel?: SubscriptionTypeModel) {
  if (isNil(subscriptionTypeModel)) {
    return false;
  }
  return (
    subscriptionTypeModel.requiredSubscriptionType === SubscriptionType.PRO &&
    subscriptionTypeModel.currentSubscription?.subscriptionType !== SubscriptionType.PRO &&
    subscriptionTypeModel.currentSubscription?.allowsDbot !== true
  );
}

export type ActivateSubscriptionContext = {
  companyName: string;
  organisationNumber: string;
  onCloseDialog: () => void;
  http: AxiosInstance;
  onFinishDialog: () => void;
};

export type ActiveShareholderRegisterSubscriptionContext = {
  onCloseDialog: () => void;
  companyName: string;
};

export class ActivateSubscriptionController {
  activateSubscriptionResult: Result | undefined;
  context: ActivateSubscriptionContext;

  constructor(activateSubscriptionContext: ActivateSubscriptionContext) {
    this.context = activateSubscriptionContext;
  }

  onActivateSubscription: () => void = async () => {
    this.activateSubscriptionResult = pending();
    try {
      await this.activateSubscription(this.context.organisationNumber);
      runInAction(() => {
        this.activateSubscriptionResult = ok();
        this.context.onFinishDialog();
      });
    } catch (error) {
      runInAction(() => {
        this.activateSubscriptionResult = failure();
      });
    }
  };

  async activateSubscription(organisationNumber: string): Promise<void> {
    const result = await this.context.http.put(
      `/payment/activate-subscription/${organisationNumber}`,
    );
    return result.data;
  }
}

export type ProductTierModel = {
  productId: string;
  tierModel?: RadioFieldModel<PricingTier | undefined>;
};

export class SubscriptionTypeSelectionController {
  onCancel: () => void;
  onSubmit: () => void;
  onFinish?: () => void;
  userContext: SubscriptionUserContext;
  shareIntervalModel: RadioFieldModel<ShareInterval>;
  subscriptionTypeModel: RadioFieldModel<RadioItem>;
  paymentTypeModel: RadioFieldModel<RadioItem>;
  availablePaymentTypes: RadioItem[];
  locale: Language;
  paymentService: PaymentService;
  createPaymentUrlResult: Result<CreatePaymentUrlResponse, TMessage> | undefined;
  createInvoiceResult: Result<void, TMessage> | undefined;
  upgradeCreditCardSubscriptionResult: Result<void, TMessage> | undefined;

  listProducts: ListProductResponse;
  tierModels: ProductTierModel[];
  billIntervalModel: RadioFieldModel<BillInterval>;
  paymentMethodModel: RadioFieldModel<PaymentType>; // rename to paymentTypeModel when the old one is removed

  constructor(parameters: {
    paymentService: PaymentService;
    onCancel: () => void;
    onSubmit?: () => void;
    onFinish?: () => void;
    locale: Language;
    userContext: {
      administratorName: string;
      currentShareholderCount: number;
      administratorEmail: string;
      companyName: string;
      organisationNumber: string;
      existingSubscription?: ProductSubscriptionResponse;
      affiliate?: Affiliate;
    };
    listProducts: ListProductResponse;
  }) {
    this.locale = parameters.locale;
    this.paymentService = parameters.paymentService;
    this.onCancel = parameters.onCancel;
    this.onSubmit = parameters.onSubmit ? parameters.onSubmit : this.defaultOnSubmit;
    this.onFinish = parameters.onFinish;
    this.userContext = parameters.userContext;
    const availableShareIntervals = this.getAvailableShareIntervals();
    this.shareIntervalModel = new RadioFieldModel<ShareInterval>({
      value: this.getInitialShareInterval(
        this.userContext.currentShareholderCount,
        availableShareIntervals,
        this.userContext.existingSubscription,
      ),
      options: availableShareIntervals,
      idFn: (item) => item.id,
      labelFn: (item) => item.label,
      onChange: (item) => {
        this.shareIntervalModel.value = item;
        this.updatePriceDescriptions();
        this.updatePaymentTypeAvailability();
      },
      disabledFn: () => this.isFormDisabled,
    });
    this.availablePaymentTypes = this.getAvailablePaymentTypes(
      this.userContext.existingSubscription,
    );
    this.paymentTypeModel = new RadioFieldModel<RadioItem>({
      value: this.getIntialPaymentType(this.userContext.existingSubscription),
      options: this.availablePaymentTypes,
      idFn: (item) => item.id,
      labelFn: (item) => item.label,
      descriptionFn: (item) => item.description,
      disabledFn: (item) => item.disabled || this.isTogglePaymentTypeDisabled,
    });
    const availableSubscriptionTypes = this.getAvailableSubscriptionTypes(
      this.userContext.existingSubscription,
    );
    this.subscriptionTypeModel = new RadioFieldModel({
      value: availableSubscriptionTypes[0],
      options: availableSubscriptionTypes,
      idFn: (item) => item.id,
      labelFn: (item) => item.label,
      descriptionFn: (item) => item.description,
      disabledFn: (item) => item.disabled || this.isFormDisabled,
    });
    this.listProducts = parameters.listProducts;
    this.tierModels = this.listProducts.products.map<ProductTierModel>((product) => {
      const tierMapping = groupByTier(product);

      const options = Object.keys(tierMapping)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map(toPricingTier)
        .filter(notUndefined) as PricingTier[];
      return {
        productId: product.productId,
        tierModel:
          options.length > 0
            ? new RadioFieldModel<PricingTier | undefined>({
                value: getInitialTierValue(
                  this.userContext,
                  options,
                  this.userContext.existingSubscription,
                ),
                options,
                idFn: identity,
                labelFn: (tier) => ({ translated: tier?.tier ?? "" }),
                disabledFn: () => this.isFormDisabled,
              })
            : undefined,
      };
    });
    this.billIntervalModel = new RadioFieldModel<BillInterval>({
      idFn: identity,
      labelFn: (option) => billIntervalLabels[option],
      value: this.userContext.existingSubscription?.billInterval ?? BillInterval.YEAR,
      options: [BillInterval.YEAR, BillInterval.MONTH],
      disabledFn: () => this.hasExistingInvoiceSubscription,
      onChange: (option) => {
        if (option === BillInterval.MONTH) {
          this.paymentMethodModel.value = PaymentType.STRIPE;
        }
      },
    });
    this.paymentMethodModel = new RadioFieldModel<PaymentType>({
      idFn: identity,
      labelFn: (option) => paymentTypeLabels[option],
      value: this.userContext.existingSubscription?.paymentMethod ?? PaymentType.STRIPE,
      options: [PaymentType.STRIPE, PaymentType.INVOICE],
      disabledFn: (option) =>
        (option === PaymentType.INVOICE && this.billIntervalModel.value === BillInterval.MONTH) ||
        this.hasExistingSubscription,
    });
    makeAutoObservable(this, {}, { autoBind: true });
    this.updatePriceDescriptions();
    this.updatePaymentTypeAvailability();
  }

  get isFormDisabled() {
    return this.hasExistingPro || this.submitFinished;
  }

  get isTogglePaymentTypeDisabled() {
    return this.hasExistingSubscription || this.submitFinished;
  }

  get hasExistingSubscription() {
    return this.userContext.existingSubscription !== undefined;
  }

  get hasExistingInvoiceSubscription() {
    return this.userContext.existingSubscription?.paymentMethod === PaymentType.INVOICE;
  }

  private getAvailableSubscriptionTypes(
    existingSubscription: ProductSubscriptionResponse | undefined,
  ): RadioItem[] {
    return [
      {
        id: SubscriptionType.PRO,
        label: { key: "payment.subscriptionpopup.subscriptionType.pro.label" },
        description: { translated: "" },
        disabled: existingSubscription?.subscriptionType === SubscriptionType.PRO,
      },
      {
        id: SubscriptionType.BASIC,
        label: { key: "payment.subscriptionpopup.subscriptionType.basic.label" },
        description: { translated: "" },
        disabled: existingSubscription !== undefined,
      },
    ];
  }

  get subscriptionStatusContext(): SubscriptionStatusContext {
    return {
      companyName: this.userContext.companyName,
      email: this.userContext.administratorEmail,
      name: this.userContext.administratorName,
      orgNo: this.userContext.organisationNumber,
      billInterval: this.userContext.existingSubscription?.billInterval,
      currentPrice: undefined,
      subscriptionType: this.userContext.existingSubscription?.subscriptionType,
      productDescription: this.userContext.existingSubscription?.productDescription,
      tier: toTier(this.userContext.existingSubscription),
      availableEnterpriseAccounts: [],
    };
  }

  private updatePriceDescriptions() {
    const basicPrice = this.shareIntervalModel.value.basicPrice;
    this.subscriptionTypeModel.options[1].description =
      basicPrice === undefined
        ? { key: "payment.subscriptionpopup.unknownPrice" }
        : {
            key: "payment.subscriptionpopup.subscriptionType.price.description",
            params: {
              price: formatPrice(this.locale, CURRENCY_NOK, basicPrice),
            },
          };

    const proPrice = this.shareIntervalModel.value.proPrice;
    this.subscriptionTypeModel.options[0].description =
      proPrice === undefined
        ? { key: "payment.subscriptionpopup.unknownPrice" }
        : {
            key: "payment.subscriptionpopup.subscriptionType.price.description",
            params: {
              price: { type: "price", value: proPrice, currency: CURRENCY_NOK },
            },
          };
  }

  closeDialog() {
    this.onCancel();
  }

  getAvailablePaymentTypes(existingSubscription?: ProductSubscriptionResponse): RadioItem[] {
    return [
      {
        id: "invoice",
        label: { key: "payment.subscriptionpopup.invoiceOption.label" },
        description: {
          key: "payment.subscriptionpopup.invoiceOption.description",
          params: {
            companyName: this.userContext.companyName,
            administratorName: this.userContext.administratorName,
            administratorEmail: this.userContext.administratorEmail,
          },
        },
        disabled: existingSubscription !== undefined,
      },
      {
        id: "card",
        label: { key: "payment.subscriptionpopup.cardOption.label" },
        description: this.userContext.affiliate
          ? { key: "payment.subscriptionpopup.cardOption.affiliate.description" }
          : { key: "payment.subscriptionpopup.cardOption.description" },
        disabled: existingSubscription !== undefined,
      },
    ];
  }

  getAvailableShareIntervals(): ShareInterval[] {
    return [
      {
        id: "0",
        label: { key: "payment.subscriptionpopup.shareinterval.1" },
        basicPrice: 99,
        basicPriceId: "f1c84c34-0f70-11ee-be56-0242ac120002",
        proPrice: 999,
        proPriceId: "ba9b7a86-0fae-11ee-be56-0242ac120002",
      },
      {
        id: "1",
        label: { key: "payment.subscriptionpopup.shareinterval.2" },
        basicPrice: 299,
        basicPriceId: "f1c85120-0f70-11ee-be56-0242ac120002",
        proPrice: 1199,
        proPriceId: "ba9b7da6-0fae-11ee-be56-0242ac120002",
      },
      {
        id: "2",
        label: { key: "payment.subscriptionpopup.shareinterval.3" },
        basicPrice: 599,
        basicPriceId: "f1c8529c-0f70-11ee-be56-0242ac120002",
        proPrice: 1499,
        proPriceId: "ba9b7edc-0fae-11ee-be56-0242ac120002",
      },
      {
        id: "3",
        label: { key: "payment.subscriptionpopup.shareinterval.4" },
        basicPrice: 799,
        basicPriceId: "f1c853fa-0f70-11ee-be56-0242ac120002",
        proPrice: 1699,
        proPriceId: "ba9b806c-0fae-11ee-be56-0242ac120002",
      },
      { id: "4", label: { key: "payment.subscriptionpopup.shareinterval.5" } },
    ];
  }

  private getIntialPaymentType(existingSubscription?: ProductSubscriptionResponse) {
    if (existingSubscription?.paymentMethod === PaymentType.STRIPE) {
      return this.availablePaymentTypes[1];
    }
    return this.availablePaymentTypes[0];
  }

  private getInitialShareInterval(
    currentShareholderCount: number,
    availableShareIntervals: ShareInterval[],
    existingSubscription?: ProductSubscriptionResponse,
  ) {
    if (existingSubscription) {
      const existingInterval: ShareInterval | undefined = availableShareIntervals.find(
        (it) =>
          it.proPriceId === existingSubscription.productPriceId ||
          it.basicPriceId === existingSubscription.productPriceId,
      );
      if (existingInterval) {
        return existingInterval;
      }
    }

    if (currentShareholderCount >= 0 && currentShareholderCount <= 3) {
      return availableShareIntervals[0];
    } else if (currentShareholderCount > 3 && currentShareholderCount <= 19) {
      return availableShareIntervals[1];
    } else if (currentShareholderCount >= 20 && currentShareholderCount <= 49) {
      return availableShareIntervals[2];
    } else if (currentShareholderCount >= 50 && currentShareholderCount <= 99) {
      return availableShareIntervals[3];
    } else {
      return availableShareIntervals[4];
    }
  }

  tierModelsForProduct(product: ProductDescription) {
    return this.tierModels.find((p) => p.productId === product.productId);
  }

  private updatePaymentTypeAvailability() {
    this.paymentTypeModel.options[1].disabled = this.shareIntervalModel.value.id === "4";
  }

  private defaultOnSubmit: () => void = async () => {
    if (this.isCreditCardSelected) {
      if (this.isSubscriptionUpgrade) {
        this.upgradeStripeSubscription();
      } else {
        this.navigateToStripePayment();
      }
    } else {
      await this.createInvoice();
      if (this.onFinish) {
        this.onFinish();
      }
    }
  };

  *createPaymentUrl(returnToUrl: string, productPriceId: string) {
    this.createPaymentUrlResult = pending();
    try {
      const response: CreatePaymentUrlResponse = yield this.paymentService.getPaymentUrl(
        this.userContext.organisationNumber,
        { returnToUrl: returnToUrl, productPriceId: productPriceId },
      );
      this.createPaymentUrlResult = ok(response);
    } catch (e) {
      this.createPaymentUrlResult = failure({ key: "general-error" });
    }
  }

  *upgradeStripeSubscription() {
    this.upgradeCreditCardSubscriptionResult = pending();
    const subscriptionId = this.userContext.existingSubscription?.subscriptionId;
    const currentProPriceId = this.shareIntervalModel.value.proPriceId;
    const currentBasicPriceId = this.shareIntervalModel.value.basicPriceId;
    if (!subscriptionId) {
      this.upgradeCreditCardSubscriptionResult = failure({
        key: "payment.subscriptionpopup.error.upgrade",
      });
      logError(`Missing existing subscription for ${this.userContext.organisationNumber}`);
      return;
    }
    if (!currentProPriceId || !currentBasicPriceId) {
      this.upgradeCreditCardSubscriptionResult = failure({
        key: "payment.subscriptionpopup.error.missingSubscriptionTypeSelection",
      });
      logError(`Missing price id selection for ${this.userContext.organisationNumber}`);
      return;
    }
    try {
      const request = {
        subscriptionId: subscriptionId,
        organisationNumber: this.userContext.organisationNumber,
        productPriceId: this.currentIsPro ? currentProPriceId : currentBasicPriceId,
      };
      const response: void = yield this.paymentService.upgradeSubscription(request);
      this.upgradeCreditCardSubscriptionResult = ok(response);
      if (this.onFinish) {
        this.onFinish();
      }
    } catch (e) {
      this.upgradeCreditCardSubscriptionResult = failure({
        key: "payment.subscriptionpopup.error.upgrade",
      });
    }
  }

  *navigateToStripePayment() {
    const currentBasicProductId = this.shareIntervalModel.value.basicPriceId;
    const currentProPriceId = this.shareIntervalModel.value.proPriceId;
    if (!currentBasicProductId || !currentProPriceId) {
      this.createPaymentUrlResult = failure({
        key: "payment.subscriptionpopup.error.illegalSubscriptionTypeSelection",
      });
      return;
    }
    yield this.createPaymentUrl(
      getReturnToAfterPaymentUrl(
        this.userContext.organisationNumber,
        encodeURIComponent(window.location.href),
      ),
      this.currentIsPro ? currentProPriceId : currentBasicProductId,
    );
    if (isOk(this.createPaymentUrlResult)) {
      window.location.href = this.createPaymentUrlResult.data.redirectToPaymentUrl;
    }
  }

  *createInvoice() {
    const currentBasicProductId = this.shareIntervalModel.value.basicPriceId;
    const currentProPriceId = this.shareIntervalModel.value.proPriceId;
    if (!currentBasicProductId || !currentProPriceId) {
      this.createInvoiceResult = failure({
        key: "payment.subscriptionpopup.error.missingSubscriptionTypeSelection",
      });
      return;
    }
    try {
      this.createInvoiceResult = pending();
      const response: void = yield this.paymentService.createInvoiceSubscription(
        this.userContext.organisationNumber,
        {
          subscriptionType: this.currentIsPro ? SubscriptionType.PRO : SubscriptionType.BASIC,
          productPriceId: this.currentIsPro ? currentProPriceId : currentBasicProductId,
        },
      );
      this.createInvoiceResult = ok(response);
    } catch (e) {
      this.createInvoiceResult = failure({ key: "payment.subscriptionpopup.error.invoice" });
    }
  }

  private get currentIsPro() {
    return this.subscriptionTypeModel.value.id === SubscriptionType.PRO;
  }

  private get isCreditCardSelected() {
    return this.paymentTypeModel.value.id === "card";
  }

  get isCreditCardPayment() {
    return this.paymentMethodModel.value === PaymentType.STRIPE;
  }

  getSubmitLabel(t: TFunction): string {
    if (this.hasExistingBasic) {
      return t("payment.subscriptionpopup.upgrade.label");
    }
    return t({
      key: "payment.subscriptionpopup.continue.label",
      params: {
        subscriptionType: this.subscriptionTypeModel.value.label,
      },
    });
  }

  get hasExistingPro() {
    return this.userContext.existingSubscription?.subscriptionType === SubscriptionType.PRO;
  }

  get submitFinished() {
    return (
      isOk(this.upgradeCreditCardSubscriptionResult) ||
      isOk(this.createPaymentUrlResult) ||
      isOk(this.createInvoiceResult)
    );
  }

  get hasExistingBasic() {
    return this.userContext.existingSubscription?.subscriptionType === SubscriptionType.BASIC;
  }

  private get isSubscriptionUpgrade() {
    return this.currentIsPro && this.hasExistingBasic;
  }

  priceRequiresCustomization(product: ProductDescription): boolean {
    const tier = this.tierModelForProduct(product);
    const isSelfService = product.activationMethod === ActivationMethod.SELF_SERVICE;
    return (
      isSelfService &&
      isNotBlank(tier?.tierModel?.value?.tier) &&
      !isPositive(calculatePrice(tier?.tierModel?.value, product, this.billIntervalModel.value))
    );
  }

  calculateMonthlyPrice(product: ProductDescription): number | undefined {
    const tier = this.tierModelForProduct(product);
    return calculatePrice(tier?.tierModel?.value, product, this.billIntervalModel.value);
  }

  validationError: Record<string, TMessage | undefined> = {};

  validationErrorForProduct(product: ProductDescription): TMessage | undefined {
    return this.validationError?.[product.productId];
  }

  validate(product: ProductDescription): boolean {
    this.validationError = {};
    const tier = this.tierModelForProduct(product);
    if (!isEmpty(tier?.tierModel?.options) && tier?.tierModel?.value === undefined) {
      this.validationError[product.productId] = {
        key: "payment.subscriptionpopup.error.missingTier",
      };
    }
    return this.validationError[product.productId] === undefined;
  }

  tierModelForProduct(product: ProductDescription) {
    return this.tierModels.find((p) => p.productId === product.productId);
  }

  findCurrentlySelectedPriceId(product: ProductDescription): string | undefined {
    const tier = this.tierModelForProduct(product);
    if (tier?.tierModel) {
      return product.prices.find(
        (price) =>
          price.billInterval === this.billIntervalModel.value &&
          price.tier?.endsWith(tier.tierModel?.value?.tier ?? ""),
      )?.productPriceId;
    }
  }

  *buyOrUpgradeCreditCardPayment(productPriceId: string) {
    if (this.userContext.existingSubscription?.paymentMethod === PaymentType.STRIPE) {
      const subscriptionId = this.userContext.existingSubscription.subscriptionId;
      const request = {
        subscriptionId: subscriptionId,
        organisationNumber: this.userContext.organisationNumber,
        productPriceId,
      };
      const response: void = yield this.paymentService.upgradeSubscription(request);
      this.upgradeCreditCardSubscriptionResult = ok(response);
    } else {
      yield this.createPaymentUrl(
        getReturnToAfterPaymentUrl(
          this.userContext.organisationNumber,
          encodeURIComponent(window.location.href),
        ),
        productPriceId,
      );
      if (isOk(this.createPaymentUrlResult)) {
        window.location.href = this.createPaymentUrlResult.data.redirectToPaymentUrl;
      }
    }
  }

  *buyProduct(product: ProductDescription) {
    if (!this.validate(product)) {
      return;
    }
    const productPriceId = this.findCurrentlySelectedPriceId(product);
    if (!productPriceId) {
      this.validationError[product.productId] = { key: "general-error" };
      logError("Missing product price id");
      return;
    }
    if (this.isCreditCardPayment) {
      yield this.buyOrUpgradeCreditCardPayment(productPriceId);
    } else {
      try {
        this.createInvoiceResult = pending();
        const response: void = yield this.paymentService.createInvoiceSubscription(
          this.userContext.organisationNumber,
          {
            subscriptionType: product.productName,
            productPriceId: productPriceId,
          },
        );
        this.createInvoiceResult = ok(response);
      } catch (e) {
        this.createInvoiceResult = failure({ key: "payment.subscriptionpopup.error.invoice" });
      }
      if (this.onFinish) {
        this.onFinish();
      }
    }
  }
}

export function calculatePrice(
  tier: PricingTier | undefined,
  product: ProductDescription,
  billInterval: BillInterval,
): number | undefined {
  if (tier === undefined) {
    return undefined;
  } else {
    const price = product.prices.find(
      (price) => price.billInterval === billInterval && price.tier?.endsWith(tier.tier),
    )?.price;
    const normalizedPrice = (price ?? 0) / 100;
    if (billInterval === BillInterval.YEAR) {
      return normalizedPrice / 12;
    }
    return normalizedPrice;
  }
}

export type SubscriptionUserContext = {
  organisationNumber: string;
  companyName: string;
  administratorName: string;
  administratorEmail: string;
  currentShareholderCount: number;
  existingSubscription?: ProductSubscriptionResponse;
  affiliate?: Affiliate;
};

export type RadioItem = {
  id: string;
  label: TMessage;
  description: TMessage;
  disabled: boolean;
};

export type ShareInterval = {
  id: string;
  label: TMessage;
  basicPrice?: number;
  basicPriceId?: string;
  proPrice?: number;
  proPriceId?: string;
};

export function toSubscriptionTypeDisplayKey(
  subscriptionType?: SubscriptionType,
): TMessageKey | undefined {
  switch (subscriptionType) {
    case SubscriptionType.BASIC:
      return "subscriptionType.basic";
    case SubscriptionType.PRO:
      return "subscriptionType.pro";
    case SubscriptionType.SHAREHOLDER_REGISTER_LEGACY:
      return "subscriptionType.shareholder_register_legacy";
  }
}

export function groupByTier(product: ProductDescription): {
  [key: string]: ProductPriceDescription[];
} {
  const tierMapping: { [key: string]: ProductPriceDescription[] } = {};
  product.prices.forEach((price) => {
    if (price.tier) {
      if (!tierMapping[price.tier]) {
        tierMapping[price.tier] = [];
      }
      if (!tierMapping[price.tier].some((p) => p.productPriceId === price.productPriceId)) {
        tierMapping[price.tier].push(price);
      }
    }
  });
  return tierMapping;
}

function notUndefined(x: unknown | undefined): boolean {
  return x !== undefined;
}

export function toTier(result?: ProductSubscriptionResponse): PricingTier | undefined {
  const price = result?.productDescription?.prices?.find(
    (p) => p.productPriceId === result?.productPriceId,
  );
  if (price) {
    return toPricingTier(price.tier ?? "");
  }
}

function toPricingTier(tier: string): PricingTier | undefined {
  if (tier.startsWith("shareholder_interval")) {
    return {
      tier: tier.replace("shareholder_interval:", ""),
      type: "shareholder_interval",
    };
  }
}

export function isSubscriptionExceeded(
  currentShareholderCount: number,
  pricingTier: PricingTier | undefined,
): boolean {
  const { end } = parseShareInterval(pricingTier?.tier);
  return end !== undefined && currentShareholderCount > end;
}

export function parseShareInterval(tier: string | undefined): {
  start: number | undefined;
  end: number | undefined;
} {
  if (!tier) {
    return { start: undefined, end: undefined };
  }
  const [start, end] = tier.split(/[-+]/);
  return { start: toNumber(start), end: toNumber(end) };
}

function toShareIntervalsPricingTier(tiers: PricingTier[], userContext: SubscriptionUserContext) {
  return tiers.find((pricing) => {
    const { start, end } = parseShareInterval(pricing.tier);
    if (!end && userContext.currentShareholderCount >= (start ?? 0)) {
      return pricing;
    }
    if (
      userContext.currentShareholderCount >= (start ?? 0) &&
      userContext.currentShareholderCount <= (end ?? 0)
    ) {
      return pricing;
    }
  });
}

export function getInitialTierValue(
  userContext: SubscriptionUserContext,
  tiers: PricingTier[],
  existingSubscription?: ProductSubscriptionResponse,
): PricingTier | undefined {
  if (tiers.length === 0 || userContext.currentShareholderCount === 0) {
    return undefined;
  }
  logDebug("existingSubscription", existingSubscription);
  if (tiers[0].type === "shareholder_interval") {
    return toShareIntervalsPricingTier(tiers, userContext);
  }
}

export const orderUpgrade =
  ({
    companyName,
    orgNo,
    email,
    name,
    productName,
    isEdit,
  }: {
    companyName: string;
    orgNo: string;
    email: string;
    name: string;
    productName: string;
    isEdit?: boolean;
  }) =>
  () =>
    window.open(
      `https://dcompany.no/kontakt-salg/?company=${companyName}&org_no=${orgNo}&email=${email}&license=${productName}&customer_name=${name}${isEdit ? "&request_type=edit" : ""}`,
    );
